import { useState, useMemo, useCallback, useEffect } from "react";
import { FilterCondition, FilterType } from "../components/FilterBar";
import {
  FILTERS_REGISTRY,
  generateFilterLabel,
  sameFilterList,
} from "./filterRegistry";
import { MF_OPTIONS } from "state/members/options";
import { FilterOption } from "components/FilterDropdown";

/**
 * Custom hook to manage filters and provide integration with options service
 */
export const useFilters = (externalFilters: FilterCondition[] = []) => {
  const [activeFilters, setActiveFilters] =
    useState<FilterCondition[]>(externalFilters);
  const options = MF_OPTIONS.useListOptions();

  // Update active filters when external filters actually change
  useEffect(() => {
    setActiveFilters((prev) =>
      sameFilterList(prev, externalFilters) ? prev : externalFilters,
    );
  }, [externalFilters]);

  // Get options for a specific filter type
  const getOptionsForFilter = useCallback(
    (filterType: FilterType): FilterOption[] => {
      const filterConfig = FILTERS_REGISTRY[filterType];

      if (!filterConfig) {
        return [];
      }

      // If the filter has static options, use those
      if (filterConfig.options) {
        return filterConfig.options;
      }

      // If the filter uses options from the options service
      if (filterConfig.optionsSource && options) {
        const sourceOptions = options[filterConfig.optionsSource];
        return sourceOptions;
      }

      return [];
    },
    [options],
  );

  // Add a new filter
  const addFilter = useCallback(
    (filter: Omit<FilterCondition, "id" | "label"> & { id?: string; label?: string }) => {
      const newFilter: FilterCondition = {
        ...filter,
        id: filter.id || `filter-${Date.now()}`,
        label: filter.label || generateFilterLabel(filter),
      };

      setActiveFilters((prev) => [...prev, newFilter]);
    },
    [],
  );

  // Remove a filter by id
  const removeFilter = useCallback((filterId: string) => {
    setActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  // Memoized: consumers put this object in `useMemo`/`useCallback` dependency
  // arrays, and a fresh object every render silently voids every one of them.
  // In the reviewer table that meant re-filtering every account in the review
  // on each keystroke of an unrelated text field. (PLO-5463)
  return useMemo(
    () => ({
      activeFilters,
      getOptionsForFilter,
      addFilter,
      removeFilter,
      clearAllFilters,
    }),
    [
      activeFilters,
      getOptionsForFilter,
      addFilter,
      removeFilter,
      clearAllFilters,
    ],
  );
};

export default useFilters;

